import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import toast from "react-hot-toast";
import { AutocompleteField, DatePickerField } from "components/UI/Form";
import * as casesApi from "../api/casesApi";
import dayjs from "dayjs";
import * as Yup from "yup";
import { FiCopy } from "react-icons/fi";

import styles from "./index.module.css";
import { WorkPacketDisputesInfo, WorkPacketFilters } from "src/types/work-packets";
import { CaseType } from "../../WorkPackets/Cases/case-types";
import { WorkPacketType } from "src/pages/UserDashboard/WorkPackets/WorkPacketType.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CaseTextInfo from "src/pages/UserDashboard/Cases/CreateCasePopup/CaseTextInfo.tsx";
import { useUserOptions } from "../../WorkPackets/useUserOptions";
import type { WorkPacketOwner } from "src/types/work-packets";
import { ActivationStatusBanner } from "../../WorkPackets/activation-status/ActivationStatusBanner";
import type { ScopeActivationInfo } from "../../WorkPackets/activation-status/activation-status-types";
import {
  ACTIVE_SCOPE_STATUS,
  evaluateScopeActivationStatus,
} from "../../WorkPackets/activation-status/activation-status-utils";
import { parseMaybeDateString } from "src/utils/parse-maybe-date-string";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { capitalizeFirst } from "src/utils/capitalize";
import { confirmAlert } from "react-confirm-alert";
import dialogCss from "src/styles/dialog.module.css";
import { CreateCaseSuccessPopup } from "./CreateCaseSuccessPopup";
import { workPackets } from "src/queries/work-packets";

const numbersRegex = /^[0-9]*$/;
const redisputeIdRegex = /^DSPT/;

function extractScopeActivationInfoFromPreCaseInfo(
  workPacketDisputesInfo: WorkPacketDisputesInfo | null | undefined,
): ScopeActivationInfo {
  if (!workPacketDisputesInfo) return ACTIVE_SCOPE_STATUS;
  return {
    isStoreActive: workPacketDisputesInfo.activation_info.is_store_active,
    deactivationDate: parseMaybeDateString(workPacketDisputesInfo.activation_info.scope_deactivation_date) ?? undefined,
  };
}

interface SunsetBannerTitleProps {
  storeName: string;
  workPacketType: WorkPacketType;
}

const SunsetBannerTitle: FC<SunsetBannerTitleProps> = ({ storeName, workPacketType }) => (
  <>
    Store {storeName} is in the sunset phase for the {workPacketType} scope
  </>
);

const SunsetBannerBody: FC = () => (
  <>
    You are about to create a case for a store that has been sunsetted. If you proceed, no new updates will be made by
    our system for this created case.
  </>
);

const DeactivationBannerTitle: FC = () => <>This store is deactivated</>;

const DeactivationBannerBody: FC = () => (
  <>
    You are about to create a case for a store that has been deactivated. If you proceed, no new updates will be made by
    our system for this created case.
  </>
);

const createInitialFormValues = (workPacketType: WorkPacketType) => ({
  caseId: "",
  caseType:
    workPacketType === WorkPacketType.CHARGEBACKS ? CaseType.CHARGEBACK_ESCALATION : CaseType.SHORTAGE_ESCALATION,
  caseCreatedAt: dayjs(),
  caseTextTitle: "",
  caseTextBody: "",
  processorId: "",
  backupProcessorId: "",
  disputeId: "",
});

type FormValues = ReturnType<typeof createInitialFormValues>;

interface CreateCasePopupProps {
  open: boolean;
  onClose: (success?: boolean) => void;
  selectedWorkPacketsIds: string[];
  excludedWorkPacketsIds: string[];
  filters: WorkPacketFilters;
  workPacketType: WorkPacketType;
}

const validationSchema = Yup.object().shape({
  caseId: Yup.string()
    .trim()
    .required("Case ID is required")
    .when(["caseType"], {
      is: (caseType: string) => caseType !== CaseType.SHORTAGE_SETTLEMENT,
      then: schema => schema.matches(numbersRegex, "Case ID must contain only numbers"),
    }),
  caseType: Yup.string().required("Case type is required"),
  caseCreatedAt: Yup.object().required("Case created date is required"),
  disputeId: Yup.string()
    .trim()
    .matches(redisputeIdRegex, 'Re-dispute ID must begin with "DSPT" (uppercase)')
    .when(["caseType"], {
      is: (caseType: string) => caseType === CaseType.SHORTAGE_ESCALATION,
      then: schema => schema.required("Re-dispute ID is required"),
    }),
});

const CreateCaseValidationMessageBanner: FC<{ values: FormValues }> = ({ values }) => {
  const paragraphs = useMemo<ReactNode[]>(() => {
    if (values.caseType === CaseType.SHORTAGE_SETTLEMENT) return EMPTY_ARRAY;
    const paragraphs: ReactNode[] = [];

    const caseId = values.caseId.trim();
    const redisputeId = values.disputeId.trim();

    if (caseId.startsWith("DSPT")) {
      paragraphs.push(
        <>
          Your Case ID input starts with "DSPT", did you mean to enter it in the <b>Re-dispute ID</b> field instead?
        </>,
      );
    }

    const validationErrors: string[] = [];
    if (caseId && !numbersRegex.test(caseId)) {
      validationErrors.push("case IDs are only allowed to consist of numbers");
    }

    if (redisputeId && !redisputeId.startsWith("DSPT")) {
      validationErrors.push('re-dispute IDs must begin with "DSPT" (uppercase)');
    }

    if (validationErrors.length > 0) {
      const formattedValidationErrors = `${capitalizeFirst(validationErrors.join(", "))}.`;
      paragraphs.push(<>{formattedValidationErrors}</>);
    }

    return paragraphs;
  }, [values]);

  if (!paragraphs.length) return null;

  return (
    <Alert severity="warning" sx={{ marginBottom: "2rem" }}>
      {paragraphs.map((message, index) => (
        <p key={index}>{message}</p>
      ))}
    </Alert>
  );
};

export const CreateCasePopup: FC<CreateCasePopupProps> = ({
  open,
  onClose,
  selectedWorkPacketsIds,
  excludedWorkPacketsIds,
  filters,
  workPacketType,
}) => {
  const queryClient = useQueryClient();

  const {
    data: workPacketDisputesInfo,
    isPending: loadingDisputes,
    isError,
  } = useQuery({
    ...workPackets.preCaseData({
      workPacketType,
      workPacketIds: selectedWorkPacketsIds,
      excludedWorkPackets: excludedWorkPacketsIds,
      filters,
    }),
    gcTime: 0,
  });

  useEffect(() => {
    if (isError) {
      toast.error("Failed to fetch data needed to create case. Please try again.");
      onClose();
    }
  }, [isError, onClose]);

  const [loading, setLoading] = useState<boolean>(false);
  const userOptions = useUserOptions(true);
  const initialValues = useMemo<FormValues>(() => createInitialFormValues(workPacketType), [workPacketType]);
  const handleSubmit = async (values: {
    caseId: string;
    caseType: string;
    caseCreatedAt: dayjs.Dayjs | null;
    caseTextTitle: string;
    caseTextBody: string;
    processorId: string;
    backupProcessorId: string;
    disputeId: string;
  }) => {
    setLoading(true);
    casesApi
      .createCase({
        caseId: values.caseId.trim(),
        caseType: values.caseType,
        caseCreatedAt: values.caseCreatedAt ? values.caseCreatedAt.format("YYYY-MM-DD") : "",
        workPacketIds: selectedWorkPacketsIds,
        excludedWorkPacketIds: excludedWorkPacketsIds,
        filters,
        workPacketType,
        caseTextTitle: values.caseTextTitle,
        caseTextBody: values.caseTextBody,
        processorId: values.processorId || null,
        backupProcessorId: values.backupProcessorId || null,
        disputeId: values.caseType === CaseType.SHORTAGE_SETTLEMENT ? null : values.disputeId.trim() || null,
      })
      .then(result => {
        confirmAlert({
          customUI: ({ onClose }) => (
            <CreateCaseSuccessPopup details={result} caseType={values.caseType as CaseType} onClose={onClose} />
          ),
          closeOnClickOutside: false,
          closeOnEscape: false,
          overlayClassName: dialogCss.dialogOverlay,
        });
        queryClient.invalidateQueries({ queryKey: ["work-packets", workPacketType] });
        queryClient.invalidateQueries({ queryKey: ["cases"] });
        onClose(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const scopeActivationInfo = extractScopeActivationInfoFromPreCaseInfo(workPacketDisputesInfo);
  const caseActivationInfo = evaluateScopeActivationStatus(scopeActivationInfo);
  const showActivationStatusWarning = caseActivationInfo.state !== "active";
  const [isWarningAcknowledged, setIsWarningAcknowledged] = useState(false);
  const shouldShowContents = !showActivationStatusWarning || isWarningAcknowledged;

  const onCloseAttempt = (isFormDirty: boolean) => {
    if (isFormDirty) {
      toast.error("You have unsaved changes. Submit or discard them before closing.");
    } else {
      onClose();
    }
  };

  if (loadingDisputes)
    return (
      <Dialog open={open} maxWidth="md" fullWidth>
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      </Dialog>
    );

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ dirty, isValid, values }) => (
        <Dialog open={open} maxWidth="md" fullWidth onClose={() => onCloseAttempt(dirty)}>
          <DialogTitle>Attach a Case ID</DialogTitle>
          <Form>
            <DialogContent>
              {workPacketDisputesInfo && (
                <Box paddingBottom={"1.5rem"}>
                  {showActivationStatusWarning && (
                    <ActivationStatusBanner
                      title={
                        caseActivationInfo.state === "inactive" ? (
                          <DeactivationBannerTitle />
                        ) : (
                          <SunsetBannerTitle
                            storeName={workPacketDisputesInfo.store_name}
                            workPacketType={workPacketType}
                          />
                        )
                      }
                      body={caseActivationInfo.state === "inactive" ? <DeactivationBannerBody /> : <SunsetBannerBody />}
                      isDeactivation={caseActivationInfo.state === "inactive"}
                      isWarningAcknowledged={isWarningAcknowledged}
                      setIsWarningAcknowledged={setIsWarningAcknowledged}
                    />
                  )}
                  {shouldShowContents && (
                    <ol
                      className={styles.logCaseSteps}
                      style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
                    >
                      {workPacketType === WorkPacketType.SHORTAGES && (
                        <>
                          <li>Select the Type of Case</li>
                          <Box marginY={".2rem"} maxWidth={250}>
                            <Field
                              name={"caseType"}
                              label="Case Type"
                              size="small"
                              fullwidth
                              component={AutocompleteField}
                              placeholder="Select a type of case"
                              getOptionLabel={(option: any) => option.title}
                              options={[
                                { value: "SHORTAGE_ESCALATION", title: "Shortage Escalation" },
                                { value: "SHORTAGE_SETTLEMENT", title: "Shortage Settlement" },
                              ]}
                            />
                          </Box>
                        </>
                      )}
                      <li>
                        Log into
                        <Button
                          className={styles.linkBtn}
                          href={"https://vendorcentral.amazon.com"}
                          target={"_blank"}
                          variant="text"
                        >
                          Amazon Vendor Central
                        </Button>
                        as Filling User: {workPacketDisputesInfo.manual_filing_user}
                      </li>
                      <li>
                        Go to{" "}
                        <Button
                          className={styles.linkBtn}
                          href={"https://vendorcentral.amazon.com/katalmonsapp/vendor/members/disputes/re/create"}
                          variant="text"
                          target={"_blank"}
                        >
                          Case Creation Page
                        </Button>
                      </li>
                      <li>Fill in the following dispute IDs:</li>
                      <OutlinedInput
                        size="small"
                        value={workPacketDisputesInfo.dispute_ids.join(", ")}
                        sx={{
                          minWidth: 250,
                        }}
                        endAdornment={
                          <InputAdornment position={"end"}>
                            <IconButton
                              onClick={() => {
                                navigator.clipboard.writeText(workPacketDisputesInfo?.dispute_ids.join(", ") ?? "");
                                toast.success("Dispute IDs copied");
                              }}
                            >
                              <FiCopy />
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      {workPacketType === WorkPacketType.SHORTAGES && (
                        <CaseTextInfo workPacketDisputesInfo={workPacketDisputesInfo} />
                      )}
                    </ol>
                  )}
                </Box>
              )}
              {shouldShowContents && (
                <>
                  <hr className={styles.hr} />
                  <CreateCaseValidationMessageBanner values={values} />
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
                    <Field
                      as={TextField}
                      name="caseId"
                      label="Case ID"
                      inputMode={values.caseType !== CaseType.SHORTAGE_SETTLEMENT ? "numeric" : "text"}
                      size="small"
                      variant="outlined"
                      placeholder="Enter Case ID"
                    />
                    {values.caseType === CaseType.SHORTAGE_ESCALATION && (
                      <Field
                        name={"disputeId"}
                        label="Re-dispute ID"
                        as={TextField}
                        size="small"
                        variant="outlined"
                        placeholder="Enter Re-dispute ID"
                      />
                    )}
                    <Field
                      name={"processorId"}
                      label="Processor"
                      component={AutocompleteField}
                      size="small"
                      options={userOptions}
                      getOptionLabel={(option: WorkPacketOwner) => option.title}
                      placeholder="Select Processor"
                    />
                    <Field
                      name={"backupProcessorId"}
                      label="Backup Processor"
                      component={AutocompleteField}
                      size="small"
                      options={userOptions}
                      getOptionLabel={(option: WorkPacketOwner) => option.title}
                      placeholder="Select Backup Processor"
                    />
                    <Field
                      name={"caseCreatedAt"}
                      label="Case Created Date"
                      component={DatePickerField}
                      placeholder="Case Created Date"
                    />
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions style={{ background: "transparent" }}>
              <Button variant="outlined" onClick={() => onClose()} disabled={loading}>
                Cancel {isValid}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!dirty || !isValid || loading}
                sx={{ paddingX: "24px" }}
              >
                <span>Attach Case ID</span>
                {loading && <CircularProgress size={14} style={{ marginLeft: "12px", color: "#101828" }} />}
              </Button>
            </DialogActions>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};
